/* Iron Lion — interactive spinning gold logo hero (Three.js, ES module)
   The Iron Lion mark as a solid, crisp gold sign. Revolves on its own; the
   user can grab and spin it (pointer drag with momentum), then it settles
   back into its idle revolve. Double-sided like a real sign: the artwork
   reads correctly from the front AND the back (no mirror text).
   Resilient: any failure leaves the static lit fallback image in place. */

import * as THREE from "https://unpkg.com/three@0.160.0/build/three.module.js";

const mount = document.querySelector("[data-medallion]");
if (mount) init(mount);

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/* Draw the logo into a square canvas, key alpha from luminance when the PNG
   has no real transparency (gold art on black keys perfectly), and remap the
   art onto a bright, high-contrast gold ramp. */
function makeGoldCanvasTexture(img, renderer) {
  const S = 1024;
  const cv = document.createElement("canvas");
  cv.width = S;
  cv.height = S;
  const ctx = cv.getContext("2d", { willReadFrequently: true });
  const scale = Math.min(S / img.width, S / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, (S - w) / 2, (S - h) / 2, w, h);

  const data = ctx.getImageData(0, 0, S, S);
  const px = data.data;

  let transparentPx = 0;
  for (let i = 3; i < px.length; i += 4) {
    if (px[i] < 128) transparentPx++;
  }
  const hasAlpha = transparentPx > px.length / 4 * 0.05; // >5% transparent = real alpha

  const dark = [140, 104, 26];
  const mid = [228, 190, 66];
  const bright = [255, 240, 180];
  for (let i = 0; i < px.length; i += 4) {
    const lum = Math.max(px[i], px[i + 1], px[i + 2]) / 255;
    if (!hasAlpha) px[i + 3] = Math.min(255, Math.round(lum * 360));
    const t = Math.pow(lum, 0.65);
    let r, g, b;
    if (t < 0.5) {
      const k = t / 0.5;
      r = dark[0] + (mid[0] - dark[0]) * k;
      g = dark[1] + (mid[1] - dark[1]) * k;
      b = dark[2] + (mid[2] - dark[2]) * k;
    } else {
      const k = (t - 0.5) / 0.5;
      r = mid[0] + (bright[0] - mid[0]) * k;
      g = mid[1] + (bright[1] - mid[1]) * k;
      b = mid[2] + (bright[2] - mid[2]) * k;
    }
    px[i] = r;
    px[i + 1] = g;
    px[i + 2] = b;
  }
  ctx.putImageData(data, 0, 0);

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
  return tex;
}

async function init(mount) {
  const logoUrl = mount.getAttribute("data-logo");
  if (!logoUrl) return;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  } catch (e) {
    return; // no WebGL — keep the fallback image
  }

  let img;
  try {
    img = await loadImage(logoUrl);
  } catch (e) {
    return; // logo unreachable — keep the fallback image
  }

  const size = () => ({ w: mount.clientWidth || 460, h: mount.clientHeight || 460 });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(size().w, size().h);
  renderer.domElement.style.touchAction = "none";
  mount.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(34, size().w / size().h, 0.1, 100);
  camera.position.set(0, 0, 6.2);

  const group = new THREE.Group();
  scene.add(group);

  const tex = makeGoldCanvasTexture(img, renderer);

  /* Solid double-sided gold sign, unlit for maximum legibility:
     — front face shows the artwork
     — back face is a second front-facing plane rotated 180°, so its front
       (with the normal texture) faces backward and reads correctly from behind
     — darker core sheets between them give the flip a hint of thickness */
  const SIZE = 3.7;
  const geo = new THREE.PlaneGeometry(SIZE, SIZE);

  const frontFace = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: tex, alphaTest: 0.5, side: THREE.FrontSide }));
  frontFace.position.z = 0.028;
  group.add(frontFace);

  const backFace = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: tex, alphaTest: 0.5, side: THREE.FrontSide }));
  backFace.rotation.y = Math.PI;
  backFace.position.z = -0.028;
  group.add(backFace);

  const coreMat = new THREE.MeshBasicMaterial({ map: tex, alphaTest: 0.5, side: THREE.DoubleSide, color: new THREE.Color(0.45, 0.45, 0.45) });
  [-0.014, 0, 0.014].forEach((z) => {
    const sheet = new THREE.Mesh(geo, coreMat);
    sheet.position.z = z;
    group.add(sheet);
  });

  // WebGL is live — retire the static fallback image
  const fallback = document.querySelector("[data-hero-lion]");
  if (fallback) fallback.style.display = "none";
  mount.classList.add("is-live");

  /* --- interaction: parallax when idle, grab-to-spin with momentum --- */
  const AUTO_SPEED = 0.01;
  const MAX_VEL = 0.35;
  let px2 = 0;
  let py2 = 0;
  let dragging = false;
  let lastX = 0;
  let vel = 0;
  const el = renderer.domElement;
  const hero = mount.closest("[data-hero]") || mount;

  el.addEventListener("pointerdown", (e) => {
    dragging = true;
    lastX = e.clientX;
    vel = 0;
    mount.classList.add("is-dragging");
    try { el.setPointerCapture(e.pointerId); } catch (err) {}
    e.preventDefault();
  });
  el.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    const dx = e.clientX - lastX;
    lastX = e.clientX;
    const d = Math.max(-MAX_VEL, Math.min(MAX_VEL, dx * 0.012));
    group.rotation.y += d;
    vel = d;
  });
  const release = () => {
    dragging = false;
    mount.classList.remove("is-dragging");
  };
  el.addEventListener("pointerup", release);
  el.addEventListener("pointercancel", release);

  if (!reduce) {
    hero.addEventListener("pointermove", (e) => {
      if (dragging) return;
      const r = hero.getBoundingClientRect();
      px2 = ((e.clientX - r.left) / r.width - 0.5) * 1.4;
      py2 = ((e.clientY - r.top) / r.height - 0.5) * 0.35;
    });
  }

  let t = 0;
  function frame() {
    requestAnimationFrame(frame);
    if (!dragging) {
      if (Math.abs(vel) > AUTO_SPEED) {
        group.rotation.y += vel; // momentum from the fling
        vel *= 0.955; // friction
      } else if (!reduce) {
        group.rotation.y += AUTO_SPEED; // settle into the idle revolve
        vel = 0;
      }
      if (!reduce) {
        t += 0.016;
        group.position.y = Math.sin(t) * 0.05;
      }
    }
    group.rotation.x += (py2 - group.rotation.x) * 0.06;
    camera.position.x += (px2 - camera.position.x) * 0.06;
    camera.lookAt(0, 0, 0);
    renderer.render(scene, camera);
  }
  frame();

  let resizeRaf;
  window.addEventListener("resize", () => {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(() => {
      const s = size();
      camera.aspect = s.w / s.h;
      camera.updateProjectionMatrix();
      renderer.setSize(s.w, s.h);
    });
  });
}
